/**
 * Modal Alerts Composable
 *
 * Manages a queue of modal alerts that require user acknowledgment.
 * Supports navigation between alerts and cross-device sync.
 */
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useNotifications } from './useNotifications'
import { useAuthStore } from '@/stores/auth'
import { activityFeedApi } from '@/plugins/api'

export interface ModalAlert {
  id: string
  type: string
  title: string
  body: string | null
  data: Record<string, unknown>
  entityType: string | null
  entityId: string | null
  actorId: string | null
  createdAt: string
}

// Shared state across components (singleton pattern)
const queue = ref<ModalAlert[]>([])
const currentIndex = ref(0)
const isInitialized = ref(false)

export function useModalAlerts() {
  const authStore = useAuthStore()

  const currentAlert = computed(() => queue.value[currentIndex.value] || null)
  const totalAlerts = computed(() => queue.value.length)
  const hasAlerts = computed(() => queue.value.length > 0)
  const canGoNext = computed(() => currentIndex.value < queue.value.length - 1)
  const canGoPrev = computed(() => currentIndex.value > 0)

  /**
   * Add alert to queue (avoid duplicates)
   */
  function addAlert(alert: ModalAlert) {
    if (!queue.value.some((a) => a.id === alert.id)) {
      queue.value.push(alert)
    }
  }

  /**
   * Remove alert from queue by ID
   */
  function removeAlert(id: string) {
    const index = queue.value.findIndex((a) => a.id === id)
    if (index >= 0) {
      queue.value.splice(index, 1)
      // Adjust currentIndex if needed
      if (currentIndex.value >= queue.value.length) {
        currentIndex.value = Math.max(0, queue.value.length - 1)
      }
    }
  }

  /**
   * Navigate to next alert
   */
  function goNext() {
    if (canGoNext.value) {
      currentIndex.value++
    }
  }

  /**
   * Navigate to previous alert
   */
  function goPrev() {
    if (canGoPrev.value) {
      currentIndex.value--
    }
  }

  /**
   * Dismiss current alert (marks as read in backend)
   */
  async function dismissCurrent() {
    const alert = currentAlert.value
    if (!alert) return

    try {
      // Mark as read via API
      await activityFeedApi.markAsRead([alert.id])
    } catch (error) {
      console.warn('[ModalAlerts] Failed to mark as read:', error)
    }

    // Remove from local queue
    removeAlert(alert.id)
  }

  /**
   * Dismiss all alerts
   */
  async function dismissAll() {
    const ids = queue.value.map((a) => a.id)
    if (ids.length === 0) return

    try {
      await activityFeedApi.markAsRead(ids)
    } catch (error) {
      console.warn('[ModalAlerts] Failed to mark all as read:', error)
    }

    queue.value = []
    currentIndex.value = 0
  }

  /**
   * Handle incoming modal:alert event from WebSocket
   */
  function handleModalAlert(event: ModalAlert) {
    console.log('[ModalAlerts] Received modal:alert:', event.id, event.type)
    addAlert(event)
  }

  /**
   * Handle modal:dismissed event (sync from another device)
   */
  function handleModalDismissed(event: { ids: string[] }) {
    console.log('[ModalAlerts] Received modal:dismissed:', event.ids.length, 'items')
    for (const id of event.ids) {
      removeAlert(id)
    }
  }

  /**
   * Handle activity:read-sync event (another device marked as read)
   */
  function handleActivityReadSync(readIds: string[]) {
    for (const id of readIds) {
      removeAlert(id)
    }
  }

  /**
   * Load pending modal alerts from API on mount
   */
  async function loadPendingAlerts() {
    if (!authStore.personId) return

    try {
      const response = await activityFeedApi.getPendingModals()
      const alerts = response.data || []

      for (const alert of alerts) {
        addAlert({
          id: alert.id,
          type: alert.type,
          title: alert.title,
          body: alert.body,
          data: alert.data || {},
          entityType: alert.entityType,
          entityId: alert.entityId,
          actorId: alert.actorId,
          createdAt: alert.createdAt as string,
        })
      }

      console.log('[ModalAlerts] Loaded', alerts.length, 'pending alerts')
    } catch (error) {
      console.warn('[ModalAlerts] Failed to load pending alerts:', error)
    }
  }

  // WebSocket subscription
  const { socket, isConnected, onActivityReadSync } = useNotifications()
  let unsubscribeReadSync: (() => void) | null = null
  let isSubscribedToModalEvents = false

  // Subscribe to modal events when socket connects
  // Using watch instead of onMounted to handle race condition where
  // socket may not be connected when ModalAlertDialog mounts
  function subscribeToModalEvents() {
    if (socket.value && !isSubscribedToModalEvents) {
      socket.value.on('modal:alert', handleModalAlert)
      socket.value.on('modal:dismissed', handleModalDismissed)
      isSubscribedToModalEvents = true
      console.log('[ModalAlerts] Subscribed to modal:alert events')
    }
  }

  function unsubscribeFromModalEvents() {
    if (socket.value && isSubscribedToModalEvents) {
      socket.value.off('modal:alert', handleModalAlert)
      socket.value.off('modal:dismissed', handleModalDismissed)
      isSubscribedToModalEvents = false
    }
  }

  // Watch for socket connection changes
  const stopWatchSocket = watch(
    isConnected,
    (connected) => {
      if (connected) {
        subscribeToModalEvents()
      } else {
        // Mark as unsubscribed so we re-subscribe on reconnect
        isSubscribedToModalEvents = false
      }
    },
    { immediate: true },
  )

  // Watch for auth state changes to load pending alerts
  // This handles the case when ModalAlertDialog mounts before auth is initialized
  const stopWatchAuth = watch(
    () => authStore.personId,
    (personId) => {
      if (personId && !isInitialized.value) {
        isInitialized.value = true
        loadPendingAlerts()
        console.log('[ModalAlerts] Auth ready, loading pending alerts')
      }
    },
    { immediate: true },
  )

  onMounted(() => {
    // Subscribe to read sync events
    unsubscribeReadSync = onActivityReadSync(handleActivityReadSync)

    // Try to subscribe if socket is already connected
    subscribeToModalEvents()
  })

  onUnmounted(() => {
    // Stop watching connection and auth
    stopWatchSocket()
    stopWatchAuth()

    // Unsubscribe from read sync
    if (unsubscribeReadSync) {
      unsubscribeReadSync()
    }

    // Unsubscribe from modal events
    unsubscribeFromModalEvents()
  })

  return {
    // State
    queue,
    currentAlert,
    currentIndex,
    totalAlerts,
    hasAlerts,
    canGoNext,
    canGoPrev,

    // Actions
    addAlert,
    removeAlert,
    goNext,
    goPrev,
    dismissCurrent,
    dismissAll,
    loadPendingAlerts,
  }
}
